import React, { Suspense, lazy, useState } from 'react';
import {
  MdInsertDriveFile,
  MdPictureAsPdf,
  MdArchitecture,
  MdBrokenImage,
  MdImage,
  MdViewInAr,
} from 'react-icons/md';
import { classifyAttachment, type AttachmentKind } from './attachmentKind';
import { useAttachmentBlobUrl } from './useAttachmentBlobUrl';
import { useInViewport } from './useInViewport';
import { formatFileSize } from '@components/Chat/helpers';
import type { PreviewableAttachment } from './types';
import { IMAGE_THUMBNAIL_MAX_BYTES, TILE_SIZE_PX } from './AttachmentTray.constants';
import { PDF_THUMBNAIL_MAX_BYTES } from '@components/PdfViewer/PdfViewer.constants';
import {
  ThumbnailTile,
  ThumbnailImage,
  ThumbnailCaption,
  ThumbnailSkeleton,
  TileGlyph,
  FileChip,
  FileChipName,
  FileChipSize,
} from './AttachmentTray.styled';

// Lazy so pdf.js stays out of the entry bundle for the majority of sessions
// that never attach a PDF.
const PdfFirstPage = lazy(() => import('@components/PdfViewer/PdfFirstPage'));

const GLYPHS: Record<AttachmentKind, React.ComponentType> = {
  image: MdImage,
  pdf: MdPictureAsPdf,
  dxf: MdArchitecture,
  model: MdViewInAr,
  other: MdInsertDriveFile,
};

/**
 * Kinds with a renderer, and the size past which rendering isn't worth it.
 * Zero means there is no renderer at all, not "only empty files".
 */
const THUMBNAIL_BUDGETS: Record<AttachmentKind, number> = {
  image: IMAGE_THUMBNAIL_MAX_BYTES,
  pdf: PDF_THUMBNAIL_MAX_BYTES,
  dxf: 0,
  // A mesh has to be parsed and rendered in WebGL before it can be shown at
  // all, which is far too much for a tile. It opens into the viewer instead.
  model: 0,
  other: 0,
};

/**
 * `chip` lets an unrenderable file take the width its name needs, which is
 * right in a message where the tray flows inline. `square` keeps every file
 * the same size instead, for a grid — at the cost of eliding longer names.
 * `line` is a chip for every file, thumbnail or not, for a one-line strip
 * under a viewer that already shows the file.
 */
export type AttachmentTileShape = 'chip' | 'square' | 'line';

interface AttachmentTileProps {
  item: PreviewableAttachment;
  /** Undefined when the file can neither be previewed nor downloaded. */
  onActivate?: () => void;
  shape?: AttachmentTileShape;
}

const AttachmentTile: React.FC<AttachmentTileProps> = ({
  item,
  onActivate,
  shape = 'chip',
}) => {
  const { ref, hasBeenNear, isNear } = useInViewport<HTMLButtonElement>();
  const [renderFailed, setRenderFailed] = useState(false);

  const kind = classifyAttachment(item.contentType, item.filename);
  const budget = THUMBNAIL_BUDGETS[kind];
  // Drawn from the reduced copy where there is one, and judged against that
  // copy's size: a scan the tile could never afford whole has a preview that
  // costs almost nothing.
  const rendered = item.preview ?? item;
  const wantsThumbnail =
    shape !== 'line' &&
    Boolean(rendered.source) &&
    budget > 0 &&
    (rendered.fileSize ?? 0) <= budget;

  const { url, isLoading, hasError } = useAttachmentBlobUrl(
    rendered.source,
    wantsThumbnail && hasBeenNear,
  );

  const interactive = Boolean(onActivate);
  const commonProps = {
    ref,
    type: 'button' as const,
    $interactive: interactive,
    onClick: onActivate,
    disabled: !interactive,
    title: item.filename,
    'data-testid': 'attachment-tile',
    'data-kind': kind,
  };

  const failed = hasError || renderFailed;

  if (!wantsThumbnail || failed) {
    // A broken-image glyph on a merely oversized file reads as corruption.
    const Glyph = failed && kind === 'image' ? MdBrokenImage : GLYPHS[kind];
    return shape === 'square' ? (
      <ThumbnailTile {...commonProps} aria-label={item.filename}>
        <TileGlyph>
          <Glyph />
        </TileGlyph>
        <ThumbnailCaption>{item.filename}</ThumbnailCaption>
      </ThumbnailTile>
    ) : (
      <FileChip {...commonProps} $shrink={shape === 'line'}>
        <Glyph />
        <FileChipName>{item.filename}</FileChipName>
        <FileChipSize>{formatFileSize(item.fileSize)}</FileChipSize>
      </FileChip>
    );
  }

  const preview = () => {
    if (isLoading || !url) return <ThumbnailSkeleton />;
    if (kind === 'pdf') {
      // A mounted Document holds a pdf.js worker for as long as it lives, so one
      // per PDF ever scrolled past grows without bound. The bytes stay cached,
      // so scrolling back re-renders from memory.
      if (!isNear) return <ThumbnailSkeleton />;
      return (
        <Suspense fallback={<ThumbnailSkeleton />}>
          <PdfFirstPage
            file={url}
            width={TILE_SIZE_PX}
            placeholder={<ThumbnailSkeleton />}
            onError={() => setRenderFailed(true)}
          />
        </Suspense>
      );
    }
    return <ThumbnailImage src={url} alt={item.filename} onError={() => setRenderFailed(true)} />;
  };

  return (
    <ThumbnailTile {...commonProps} aria-label={item.filename}>
      {preview()}
      <ThumbnailCaption>{item.filename}</ThumbnailCaption>
    </ThumbnailTile>
  );
};

export default AttachmentTile;
